import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import { useState, type ReactElement } from 'react'

import {
  LockUserDialog,
  UserDetailLayout,
  UserDetailSkeleton,
  userDetailQuery,
  useUserDetailSections,
} from '~/features/users'
import { UserAddonsTab } from '~/features/users/components/UserAddonsTab'

/**
 * `/admin/users/$userId` — User detail page.
 *
 * Route-page replacement for the legacy `UserDetailsSheet` right-side sheet.
 * Per the cross-cutting convention "all detail surfaces are dedicated route
 * pages, no detail modals", every drill-in from the Users list arrives here.
 *
 * The visual layout is identical to the sheet (tabs, status chips, action
 * footer) — only the chrome changes. We reuse `UserDetailLayout` (renamed
 * from the sheet's inner `SheetContent`) which omits the close affordance
 * when `onClose` is unset.
 */
export const Route = createFileRoute('/admin/users/$userId')({
  component: UserDetailPage,
})

function UserDetailPage(): ReactElement {
  const { userId } = Route.useParams()
  const [tab, setTab] = useState<
    'profile' | 'teams' | 'subscriptions' | 'activity' | 'audit' | 'notes'
  >('profile')
  const [lockDialogOpen, setLockDialogOpen] = useState(false)

  const detailQuery = useQuery(userDetailQuery(userId))
  const sections = useUserDetailSections(userId)

  return (
    <div className="ka-page">
      <header className="ka-page-section">
        <Link
          to="/admin/users"
          className="ka-btn ka-btn--ghost ka-btn--sm"
          aria-label="Back to user list"
        >
          <ArrowLeft size={14} aria-hidden="true" /> Users
        </Link>
      </header>

      {detailQuery.isPending ? (
        <UserDetailSkeleton titleId="user-detail-title" />
      ) : detailQuery.isError ? (
        <div className="ka-card ka-card--error" role="alert">
          <div className="ka-empty">
            <div className="ka-empty-title">Couldn't load user</div>
            <div className="ka-empty-body">
              {detailQuery.error instanceof Error
                ? detailQuery.error.message
                : 'Unknown error'}
            </div>
          </div>
        </div>
      ) : (
        <>
          <UserDetailLayout
            detail={detailQuery.data}
            sections={sections}
            tab={tab}
            onTabChange={setTab}
            onLock={() => setLockDialogOpen(true)}
            titleId="user-detail-title"
          />

          {sections.relationships.data?.memberships.some(
            (membership) => membership.relationshipStatus === 'active',
          ) ? (
            <div className="ka-page-section">
              <UserAddonsTab
                memberships={sections.relationships.data.memberships.filter(
                  (membership) =>
                    membership.relationshipStatus === 'active',
                )}
              />
            </div>
          ) : null}
        </>
      )}

      {detailQuery.data ? (
        <LockUserDialog
          userId={detailQuery.data.user.id}
          userEmail={detailQuery.data.user.email}
          open={lockDialogOpen}
          onClose={() => setLockDialogOpen(false)}
        />
      ) : null}
    </div>
  )
}
